import React, { useState, useContext, useEffect, useRef } from "react";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import {
    LottieLoadingIndicator,
    Toast,
    SettingsActionButton,
    SettingsInfoBox,
    SettingsPanelCaption,
    SettingsPanelHeader,
    SettingsPanelStack,
    SettingsPanelTitle,
    SettingsSectionBody,
    SettingsSectionCard,
    SettingsSectionHeader,
    SettingsSectionTitle
} from "src/components";

import { SettingContext } from "../SettingContext";
import styled from "styled-components";
import BankDetailsForm from "./BankDetailsForm";
import BankDetailsModal from "../components/BankDetailsModal";
import RenewalCommissionTable from "../components/RenewalCommissionTable";

const HeaderDescription = styled.div`
    font-size: 14px;
    color: ${p => p.color || "#808080"};
    opacity: 1;
    font-weight: ${p => p.weight || "200"};
    font-family: Manrope;
    margin: 1em 0;
`;

const OverviewGrid = styled.div`
    width: 100%;
    grid-template-columns: minmax(0, 1fr) minmax(24rem, 34rem);
    display: grid;
    gap: 1rem;
    align-items: start;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
`;

const OverviewCopy = styled.div`
    min-width: 0;
`;

const BankButtonIcon = styled.span`
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: 8px;
    background: #FFFFFF;
    color: rgba(0, 0, 0, 0.5);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
`;

const MonthSection = styled.div`
    justify-self: end;
    max-width: 34rem;
    width: 100%;
    box-sizing: border-box;
       
    @media (max-width: 768px) {
        justify-self: start;
        margin-left: 0;
        width: 100%;
    }

`;

const StatBox = styled.div`
    background: #FFFFFF;
    box-shadow: none;
    border: 1px solid #D8DEEC;
    border-radius: 8px;
    min-height: 6.25rem;
    padding: 1rem;
    box-sizing: border-box;

    display: flex;
    flex-direction: column;
    justify-content: center;

`;

const MonthRow = styled.div`
    width: 100%;
    background: #F3F5FB;
    box-shadow: none;
    border: 1px solid #D8DEEC;
    border-radius: 8px;
    padding: 1rem;
    box-sizing: border-box;
`;

const BoxesRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const DropdownContainer = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: 1rem;
`;

const DropdownHeader = styled.div`
    min-height: 3rem;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
    background: #FFFFFF;
    box-shadow: none;
    border: 1px solid #D8DEEC;
    border-radius: 8px;
    box-sizing: border-box;
`;

const StatTitle = styled.div`
    text-align: left;
    font: normal normal 700 0.75rem/1.25rem Manrope;
    letter-spacing: 0px;
    color: #171A22;
    opacity: 1;
`;

const StatValue = styled.div`
    text-align: left;
    font: normal normal 800 1.75rem/2rem Manrope;
    letter-spacing: 0px;
    color: #11141A;
    opacity: 1;
`;

const DropdownLabelWrapper = styled.span`
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    white-space: nowrap; /* prevents awkward line breaks */
    align-items: center;
`;

const DropdownList = styled.ul`
    position: absolute;
    top: calc(100% + 4px);
    width: 100%;
    background: white;
    border: 1px solid #D8DEEC;
    border-radius: 8px;
    box-shadow: 0px 6px 10px #0000001A;
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
`;

const DropdownItem = styled.li`
    padding: 10px;
    cursor: pointer;
    font-size: 15px;
    display: flex;
    justify-content: space-between;

    border-bottom: 1px solid #D8DEEC;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #F0E9F7;
    }
`;

function RenewalAndEarning() {
    const { token } = useContext(SettingContext);
    const [isStatsLoading, setIsStatsLoading] = useState(true);
    const [isTableLoading, setTableLoading] = useState(true); /* default is true because there is a mini second that I can see the commission table */
    const [expiredLicenses, setExpiredLicenses] = useState(0);
    const [paidLicenses, setPaidLicenses] = useState(0);
    const [trialsStarted, setTrialsStarted] = useState(0);
    const [hasTrial, setHasTrial] = useState(false);
    const [earnings, setEarnings] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState("thisMonth");

    const formatDateLabel = (type) => {
        const date = new Date();
        switch (type) {
            case "thisMonth":
            return date.toLocaleString("en-US", { month: "long", year: "numeric" });

            case "lastMonth":
            const lastMonth = new Date(date.getFullYear(), date.getMonth() - 1);
            return lastMonth.toLocaleString("en-US", { month: "long", year: "numeric" });

            case "thisYear":
            return date.getFullYear();

            case "lastYear":
            return date.getFullYear() - 1;

            default:
            return "";
        }
    };

    useEffect(() => {
        getCompanyClientLicense(selectedPeriod);
        getCompanyEarnings();
    }, []);

    useEffect(() => {
        getCompanyClientLicense(selectedPeriod);
    }, [selectedPeriod]);
    
    const getCompanyClientLicense = (period = "thisMonth") => {
        setIsStatsLoading(true); 
        axios.get(`${API_URL}/company/licenseRenewalStats`, {
            headers: {
                Authorization: "Bearer " + token
            },
            params: { period }
        }).then(res => {
            setPaidLicenses(res.data.paidLicenses || 0);
            setTrialsStarted(res.data.trialsStarted || 0);
            setExpiredLicenses(res.data.expiredLicenses || 0);
            setHasTrial(res.data.hasTrial || false);
        }).catch(error => {
            return Toast.error(error.getApiErrorDetails().message);
        }).finally(() => {
            setIsStatsLoading(false);
        });
    };

    const getCompanyEarnings = () => {
        setTableLoading(true);

        axios.get(`${API_URL}/company/earningDetails`, {
            headers: {
                Authorization: "Bearer " + token
            }
        }).then(res => {
            setEarnings(res.data.earnings);
        }).catch(error => {
            return Toast.error(error.getApiErrorDetails().message);
        }).finally(() => {
            setTableLoading(false);
        });
    };
    
    const addBankDetails = () => {
        const modalHeader = 
            <React.Fragment>
                Earnings Bank Account Details
                <HeaderDescription>
                    Please kindly provide the bank account information and invoice statement details <br/> 
                    below, to enable us to deposit your earnings to the designated account and email <br/> 
                    you statements.
                </HeaderDescription>
                <HeaderDescription color="#8551AE" weight="400">
                    *You can update your bank account details at anytime by in Account Settings &gt; <br/> Renewals & Earnings..
                </HeaderDescription>
            </React.Fragment>;
                            
        const addBankDetailsForm = <React.Fragment> 
            <BankDetailsForm token={token} />
        </React.Fragment>;

        return BankDetailsModal.open(
            modalHeader,
            addBankDetailsForm,
            false,
            { width: "80vw", maxWidth: "1340px" } 
        );
    };

    /** RENDER */

    const pageHeadline = () => {
        return (
            <OverviewCopy>
                <SettingsPanelTitle>
                    Renewal & Earnings
                </SettingsPanelTitle>
                <SettingsPanelCaption>
                    Track your earnings from client trial license renewals and view statements. Manage your bank details
                    for earnings payments via the Earnings Bank Account button.
                </SettingsPanelCaption>

                <SettingsActionButton marginTop="1rem" onClick={() => {addBankDetails();}}>
                    <BankButtonIcon>$</BankButtonIcon>
                    Bank Account Details
                </SettingsActionButton>
            </OverviewCopy>
        );
    };

    return (
        <SettingsPanelStack>
            <SettingsPanelHeader withDivider>
                <OverviewGrid>
                    {pageHeadline()}
                    <MonthSection hasTrial={hasTrial}>
                        <MonthRow>
                            <CustomDropdown
                                value={selectedPeriod}
                                onChange={setSelectedPeriod}
                                formatDateLabel={formatDateLabel}
                            />
                            <BoxesRow>
                                {isStatsLoading ? (
                                    <LottieLoadingIndicator width="50px" height="50px" style={{ minHeight: "0px", marginTop: "-23px" }} />
                                ) : (
                                    <>
                                        {hasTrial && (
                                            <StatBox>
                                                <StatTitle>Trial Started</StatTitle>
                                                <StatValue>{trialsStarted}</StatValue>
                                            </StatBox>
                                        )}
                                        <StatBox>
                                            <StatTitle>Paid Licenses</StatTitle>
                                            <StatValue>{paidLicenses}</StatValue>
                                        </StatBox>
                                        <StatBox>
                                            <StatTitle>Expired Licenses</StatTitle>
                                            <StatValue>{expiredLicenses}</StatValue>
                                        </StatBox>
                                    </>
                                )}
                            </BoxesRow>
                        </MonthRow>
                    </MonthSection>
                </OverviewGrid>
            </SettingsPanelHeader>

            <SettingsSectionCard>
                <SettingsSectionHeader>
                    <SettingsSectionTitle>Earnings Statements</SettingsSectionTitle>
                </SettingsSectionHeader>
                <SettingsSectionBody>
                    {isTableLoading && (
                        <SettingsInfoBox justifyContent="center">
                            <LottieLoadingIndicator />
                        </SettingsInfoBox>
                    )}

                    {!isTableLoading && <RenewalCommissionTable earnings={earnings || []} token={token}/> }
                </SettingsSectionBody>
            </SettingsSectionCard>
        </SettingsPanelStack>
    );
}

function CustomDropdown({ value, onChange, formatDateLabel }) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const options = [
        { key: "thisMonth", label: "This Month" },
        { key: "lastMonth", label: "Last Month" },
        { key: "thisYear", label: "This Year" },
        { key: "lastYear", label: "Last Year" }
    ];
    const selectedOption = options.find(o => o.key === value);

    useEffect(() => {
        const handleOutsideClick = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    return (
        <DropdownContainer ref={dropdownRef}>
            <DropdownHeader onClick={() => setOpen(o => !o)}>
                <DropdownLabelWrapper>

                    <span style={{ marginRight: 6 }}>{selectedOption.label}</span>
                    <span style={{ color: "#8551AE", marginLeft: "2px" }}>
                        {formatDateLabel(selectedOption.key)}
                    </span>
                </DropdownLabelWrapper>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 10 10"
                    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "8px" }}
                >
                    <polyline points="1,3 5,7 9,3" fill="none" stroke="#000" strokeWidth="1.2" />
                </svg>
            </DropdownHeader>

            {open && (
                <DropdownList>
                    {options.map(option => (
                        <DropdownItem
                            key={option.key}
                            onClick={() => {
                                onChange(option.key);
                                setOpen(false);
                            }}
                        >
                            <span>{option.label}</span>
                        </DropdownItem>
                    ))}
                </DropdownList>
            )}
        </DropdownContainer>
    );
}


export default RenewalAndEarning;
